// ============================================================================
// ATS SCORER - Client-Side Only
// Calculates ATS score based on rule-based logic (NO AI)
// ============================================================================

import { detectSections, detectFormattingIssues, estimatePageCount, hasBulletPoints, countActionVerbs } from './resumeParser';
import { matchKeywords } from './keywordMatcher';

/**
 * Score Section A: Keyword Matching (40 points)
 * @param {Object} keywordMatch - Match results from keywordMatcher
 * @param {string} resumeText - Resume text for frequency analysis
 * @returns {Object} - Score and details
 */
const scoreKeywordMatching = (keywordMatch, resumeText) => {
  const maxScore = 40;

  if (!keywordMatch || keywordMatch.totalKeywords === 0) {
    // No job description - score based on resume content quality
    const wordCount = resumeText.split(/\s+/).length;
    const hasNumbers = /\d+/.test(resumeText);
    const hasPercentages = /%/.test(resumeText);
    const technicalTerms = (resumeText.match(/\b(developed|designed|implemented|created|managed|led|built|optimized|improved|increased|reduced|achieved)\b/gi) || []).length;

    let baseScore = 15; // Minimum for having content
    if (wordCount > 200) baseScore += 5;
    if (wordCount > 400) baseScore += 5;
    if (hasNumbers) baseScore += 3;
    if (hasPercentages) baseScore += 2;
    if (technicalTerms > 5) baseScore += 5;
    if (technicalTerms > 10) baseScore += 5;

    return {
      score: Math.min(baseScore, maxScore),
      maxScore,
      percentage: Math.round((baseScore / maxScore) * 100),
      details: {
        matched: 0,
        total: 0,
        matchedKeywords: [],
        missingKeywords: [],
        note: 'No job description provided - scored based on content quality'
      }
    };
  }

  // Calculate keyword density and relevance
  const matchPercentage = keywordMatch.matchPercentage;
  let score = (matchPercentage / 100) * maxScore;

  // Bonus points for keyword frequency (shows emphasis)
  let frequencyBonus = 0;
  const lowerResume = resumeText.toLowerCase();
  keywordMatch.matched.slice(0, 10).forEach(keyword => {
    const occurrences = (lowerResume.match(new RegExp(`\\b${keyword}\\b`, 'gi')) || []).length;
    if (occurrences >= 3) frequencyBonus += 0.5; // Shows strong focus
  });

  score += Math.min(frequencyBonus, 5); // Max 5 bonus points

  // Penalty for very low match rate
  if (matchPercentage < 30) {
    score *= 0.7; // 30% penalty for poor matching
  }

  return {
    score: Math.min(Math.round(score), maxScore),
    maxScore,
    percentage: matchPercentage,
    details: {
      matched: keywordMatch.matchedCount,
      total: keywordMatch.totalKeywords,
      matchedKeywords: keywordMatch.matched.slice(0, 10),
      missingKeywords: keywordMatch.missing.slice(0, 10),
      frequencyBonus: Math.min(frequencyBonus, 5)
    }
  };
};

/**
 * Score Section B: Section Presence (25 points)
 * @param {Object} sections - Detected sections from resumeParser
 * @param {string} resumeText - Full resume text for quality analysis
 * @returns {Object} - Score and details
 */
const scoreSectionPresence = (sections, resumeText) => {
  const sectionScores = {
    hasExperience: 7,
    hasEducation: 5,
    hasSkills: 7,
    hasProjects: 3,
    hasCertifications: 2,
    hasSummary: 1
  };

  let score = 0;
  const present = [];
  const missing = [];
  const qualityNotes = [];

  Object.entries(sectionScores).forEach(([section, points]) => {
    if (sections[section]) {
      let sectionScore = points;

      // Analyze section quality for additional/reduced points
      const sectionName = section.replace('has', '').toLowerCase();
      const sectionRegex = new RegExp(`(${sectionName}|${sectionName.slice(0, -1)})[:\\s]([\\s\\S]{0,500})`, 'i');
      const sectionMatch = resumeText.match(sectionRegex);

      if (sectionMatch && sectionMatch[2]) {
        const sectionContent = sectionMatch[2];
        const contentLength = sectionContent.trim().length;

        // Quality adjustments
        if (section === 'hasExperience') {
          const hasDates = /\d{4}|\d{1,2}\/\d{4}|Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec/i.test(sectionContent);
          const hasCompanyNames = /\b[A-Z][a-z]+\s+([A-Z][a-z]+\s*){0,3}(Inc|LLC|Corp|Ltd|Co|Company|Technologies|Solutions)\b/.test(sectionContent);
          const hasQuantifiableResults = /\d+%|\$\d+|increased|decreased|improved|reduced|saved|generated/i.test(sectionContent);

          if (!hasDates) {
            sectionScore -= 2;
            qualityNotes.push('Experience section missing dates');
          }
          if (!hasCompanyNames) {
            sectionScore -= 1;
            qualityNotes.push('Experience section missing clear company names');
          }
          if (hasQuantifiableResults) {
            sectionScore += 1;
            qualityNotes.push('Experience section includes quantifiable results (+1)');
          }
          if (contentLength < 100) {
            sectionScore -= 1;
            qualityNotes.push('Experience section seems too brief');
          }
        }

        if (section === 'hasSkills') {
          const skillCount = (sectionContent.match(/,/g) || []).length + 1;
          if (skillCount < 5) {
            sectionScore -= 2;
            qualityNotes.push('Skills section has very few skills listed');
          } else if (skillCount > 15) {
            sectionScore += 1;
            qualityNotes.push('Skills section is comprehensive (+1)');
          }
        }

        if (section === 'hasEducation') {
          const hasDegree = /bachelor|master|phd|doctorate|associate|b\.s\.|m\.s\.|mba/i.test(sectionContent);
          const hasInstitution = /university|college|institute|school/i.test(sectionContent);
          if (!hasDegree) {
            sectionScore -= 1;
            qualityNotes.push('Education section missing degree type');
          }
          if (!hasInstitution) {
            sectionScore -= 1;
            qualityNotes.push('Education section missing institution');
          }
        }
      }

      score += Math.max(0, sectionScore);
      present.push({
        name: section.replace('has', ''),
        points: Math.max(0, sectionScore),
        originalPoints: points
      });
    } else {
      missing.push({
        name: section.replace('has', ''),
        points
      });
    }
  });

  const maxScore = 25;

  return {
    score: Math.min(score, maxScore),
    maxScore,
    percentage: Math.round((score / maxScore) * 100),
    details: {
      present,
      missing,
      qualityNotes
    }
  };
};

/**
 * Score Section C: Formatting & ATS Friendliness (20 points)
 * @param {Object} formattingIssues - Detected formatting issues
 * @param {string} resumeText - Resume text for additional checks
 * @returns {Object} - Score and details
 */
const scoreFormatting = (formattingIssues, resumeText) => {
  const maxScore = 20;
  let deductions = 0;
  const issues = [];
  const positives = [];

  // Check for ATS-problematic elements
  if (formattingIssues.hasTables) {
    deductions += 4;
    issues.push({ issue: 'Tables detected - may not parse correctly', deduction: 4 });
  }

  if (formattingIssues.hasIcons) {
    deductions += 3;
    issues.push({ issue: 'Icons/special characters detected', deduction: 3 });
  }

  if (formattingIssues.hasComplexLayout) {
    deductions += 4;
    issues.push({ issue: 'Complex layout (possible multi-column)', deduction: 4 });
  }

  // Additional formatting checks
  const lines = resumeText.split('\n');
  const avgLineLength = resumeText.length / lines.length;

  // Check for overly long lines (might indicate no proper formatting)
  if (avgLineLength > 200) {
    deductions += 2;
    issues.push({ issue: 'Lines appear too long - poor formatting', deduction: 2 });
  }

  // Check for proper use of whitespace
  const emptyLineRatio = lines.filter(line => line.trim() === '').length / lines.length;
  if (emptyLineRatio > 0.5) {
    deductions += 2;
    issues.push({ issue: 'Excessive whitespace detected', deduction: 2 });
  } else if (emptyLineRatio > 0.2 && emptyLineRatio < 0.5) {
    positives.push({ positive: 'Good use of whitespace for readability', bonus: 0 });
  }

  // Check for contact info
  const hasEmail = /@[\w.-]+\.\w+/.test(resumeText);
  const hasPhone = /\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}|\+\d{1,3}[-.\s]?\d+/.test(resumeText);

  if (hasEmail && hasPhone) {
    positives.push({ positive: 'Complete contact information found', bonus: 2 });
  } else if (hasEmail || hasPhone) {
    positives.push({ positive: 'Contact information found', bonus: 1 });
  } else {
    deductions += 3;
    issues.push({ issue: 'Missing contact information (email/phone)', deduction: 3 });
  }

  // Check for consistent formatting markers
  const bulletCount = (resumeText.match(/[•●○◦▪▫]/g) || []).length;
  const dashCount = (resumeText.match(/^\s*[-–—]\s/gm) || []).length;
  const asteriskCount = (resumeText.match(/^\s*\*\s/gm) || []).length;

  const totalBullets = bulletCount + dashCount + asteriskCount;
  if (totalBullets > 5) {
    positives.push({ positive: 'Good use of bullet points', bonus: 1 });
  }

  // Check for all caps (screaming - bad practice)
  const allCapsWords = (resumeText.match(/\b[A-Z]{4,}\b/g) || []).length;
  if (allCapsWords > 10) {
    deductions += 2;
    issues.push({ issue: 'Excessive use of ALL CAPS', deduction: 2 });
  }

  // Calculate bonuses
  const bonusPoints = positives.reduce((sum, p) => sum + (p.bonus || 0), 0);

  const score = Math.max(0, Math.min(maxScore, maxScore - deductions + bonusPoints));

  return {
    score,
    maxScore,
    percentage: Math.round((score / maxScore) * 100),
    details: {
      issues,
      positives,
      totalDeductions: deductions,
      totalBonuses: bonusPoints
    }
  };
};

/**
 * Score Section D: Resume Structure & Quality (15 points)
 * @param {string} resumeText - Resume text
 * @param {number} pageCount - Estimated page count
 * @param {boolean} hasBullets - Has bullet points
 * @param {number} actionVerbCount - Count of action verbs
 * @returns {Object} - Score and details
 */
const scoreStructureQuality = (resumeText, pageCount, hasBullets, actionVerbCount) => {
  const maxScore = 15;
  let score = 0;
  const details = [];

  // Page length (ideal: 1-2 pages) - up to 4 points
  const wordCount = resumeText.split(/\s+/).length;
  if (pageCount >= 1 && pageCount <= 2) {
    if (wordCount >= 300 && wordCount <= 800) {
      score += 4;
      details.push({ criteria: `Optimal length (${pageCount} pages, ${wordCount} words)`, points: 4, status: 'pass' });
    } else if (wordCount < 300) {
      score += 2;
      details.push({ criteria: `Short resume (${wordCount} words)`, points: 2, status: 'warning' });
    } else {
      score += 3;
      details.push({ criteria: `Good length (${pageCount} pages)`, points: 3, status: 'pass' });
    }
  } else if (pageCount === 3) {
    score += 2;
    details.push({ criteria: 'Acceptable length (3 pages)', points: 2, status: 'warning' });
  } else if (pageCount > 3) {
    score += 1;
    details.push({ criteria: `Too long (${pageCount} pages)`, points: 1, status: 'fail' });
  } else {
    details.push({ criteria: `Too short (${pageCount} page)`, points: 0, status: 'fail' });
  }

  // Bullet points usage and quality - up to 4 points
  if (hasBullets) {
    const bulletMatches = resumeText.match(/[•●○◦▪▫-]\s+.+/g) || [];
    const avgBulletLength = bulletMatches.length > 0
      ? bulletMatches.reduce((sum, b) => sum + b.length, 0) / bulletMatches.length
      : 0;

    if (bulletMatches.length >= 10 && avgBulletLength > 50 && avgBulletLength < 200) {
      score += 4;
      details.push({ criteria: `Well-structured bullet points (${bulletMatches.length} bullets)`, points: 4, status: 'pass' });
    } else if (bulletMatches.length >= 5) {
      score += 3;
      details.push({ criteria: `Uses bullet points (${bulletMatches.length} bullets)`, points: 3, status: 'pass' });
    } else {
      score += 2;
      details.push({ criteria: `Few bullet points (${bulletMatches.length} bullets)`, points: 2, status: 'warning' });
    }
  } else {
    details.push({ criteria: 'Missing bullet points', points: 0, status: 'fail' });
  }

  // Action verbs and quantifiable achievements - up to 5 points
  const achievementWords = (resumeText.match(/\d+%|increased|decreased|improved|reduced|saved|generated|grew|boosted|enhanced/gi) || []).length;
  const numbers = (resumeText.match(/\d+/g) || []).length;

  let contentQualityScore = 0;
  if (actionVerbCount >= 15) {
    contentQualityScore += 2;
    details.push({ criteria: `Excellent action verb usage (${actionVerbCount} found)`, points: 2, status: 'pass' });
  } else if (actionVerbCount >= 8) {
    contentQualityScore += 1.5;
    details.push({ criteria: `Good action verb usage (${actionVerbCount} found)`, points: 1.5, status: 'pass' });
  } else if (actionVerbCount >= 5) {
    contentQualityScore += 1;
    details.push({ criteria: `Moderate action verbs (${actionVerbCount} found)`, points: 1, status: 'warning' });
  } else {
    details.push({ criteria: `Few action verbs (${actionVerbCount} found)`, points: 0, status: 'fail' });
  }

  if (achievementWords >= 5) {
    contentQualityScore += 2;
    details.push({ criteria: `Quantifiable achievements present (${achievementWords} instances)`, points: 2, status: 'pass' });
  } else if (achievementWords >= 3) {
    contentQualityScore += 1;
    details.push({ criteria: `Some quantifiable achievements (${achievementWords} instances)`, points: 1, status: 'warning' });
  }

  if (numbers >= 10) {
    contentQualityScore += 1;
    details.push({ criteria: 'Good use of metrics and numbers', points: 1, status: 'pass' });
  }

  score += Math.min(contentQualityScore, 5);

  // Professional language check - up to 2 points
  const informalWords = (resumeText.match(/\b(gonna|wanna|kinda|sorta|yeah|yep|nope|stuff|things|basically|literally)\b/gi) || []).length;
  const passiveVoice = (resumeText.match(/\b(was|were|been|being)\s+(given|made|done|created|developed|implemented)\b/gi) || []).length;

  if (informalWords === 0 && passiveVoice < 3) {
    score += 2;
    details.push({ criteria: 'Professional language throughout', points: 2, status: 'pass' });
  } else if (informalWords <= 2) {
    score += 1;
    details.push({ criteria: 'Mostly professional language', points: 1, status: 'warning' });
  } else {
    details.push({ criteria: 'Informal language detected', points: 0, status: 'fail' });
  }

  return {
    score: Math.round(score),
    maxScore,
    percentage: Math.round((score / maxScore) * 100),
    details
  };
};

/**
 * Calculate overall ATS score
 * @param {string} resumeText - Resume text
 * @param {string} jobDescription - Job description (optional)
 * @param {Object} keywords - Extracted keywords (optional)
 * @returns {Object} - Complete ATS score results
 */
export const calculateATSScore = (resumeText, jobDescription = '', keywords = null) => {
  if (!resumeText) {
    return {
      totalScore: 0,
      maxScore: 100,
      percentage: 0,
      grade: 'F',
      color: '#EF4444',
      breakdown: null,
      suggestions: ['Please provide a resume to analyze']
    };
  }

  // Detect sections
  const sections = detectSections(resumeText);

  // Detect formatting issues
  const formattingIssues = detectFormattingIssues(resumeText);

  // Get structure metrics
  const pageCount = estimatePageCount(resumeText);
  const hasBullets = hasBulletPoints(resumeText);
  const actionVerbCount = countActionVerbs(resumeText);

  // Calculate scores
  let keywordScore;
  if (keywords && jobDescription) {
    const keywordMatch = matchKeywords(resumeText, keywords);
    keywordScore = scoreKeywordMatching(keywordMatch, resumeText);
  } else {
    // No job description provided - score based on content quality
    keywordScore = scoreKeywordMatching(null, resumeText);
  }

  const sectionScore = scoreSectionPresence(sections, resumeText);
  const formattingScore = scoreFormatting(formattingIssues, resumeText);
  const structureScore = scoreStructureQuality(resumeText, pageCount, hasBullets, actionVerbCount);

  // Calculate total score
  const totalScore = Math.min(100,
    keywordScore.score +
    sectionScore.score +
    formattingScore.score +
    structureScore.score
  );

  // Determine grade and color
  let grade, color;
  if (totalScore >= 90) {
    grade = 'A+';
    color = '#10B981';
  } else if (totalScore >= 80) {
    grade = 'A';
    color = '#10B981';
  } else if (totalScore >= 70) {
    grade = 'B';
    color = '#22C55E';
  } else if (totalScore >= 60) {
    grade = 'C';
    color = '#EAB308';
  } else if (totalScore >= 50) {
    grade = 'D';
    color = '#F59E0B';
  } else {
    grade = 'F';
    color = '#EF4444';
  }

  // Generate suggestions
  const suggestions = generateSuggestions(
    keywordScore,
    sectionScore,
    formattingScore,
    structureScore,
    sections,
    formattingIssues,
    hasBullets,
    actionVerbCount,
    pageCount
  );

  return {
    totalScore: Math.round(totalScore),
    maxScore: 100,
    percentage: Math.round(totalScore),
    grade,
    color,
    breakdown: {
      keywordMatching: keywordScore,
      sectionPresence: sectionScore,
      formatting: formattingScore,
      structure: structureScore
    },
    suggestions,
    hasJobDescription: !!(keywords && jobDescription)
  };
};

/**
 * Generate actionable suggestions
 * @returns {string[]} - Array of suggestions
 */
const generateSuggestions = (
  keywordScore,
  sectionScore,
  formattingScore,
  structureScore,
  sections,
  formattingIssues,
  hasBullets,
  actionVerbCount,
  pageCount
) => {
  const suggestions = [];

  // Keyword suggestions
  if (keywordScore.score < 20 && keywordScore.details.missingKeywords.length > 0) {
    suggestions.push({
      category: 'Keywords',
      priority: 'high',
      text: `Add missing keywords: ${keywordScore.details.missingKeywords.slice(0, 5).join(', ')}`,
      impact: 'High impact on ATS score'
    });
  }

  // Section suggestions
  if (!sections.hasSkills) {
    suggestions.push({
      category: 'Sections',
      priority: 'high',
      text: 'Add a dedicated Skills section',
      impact: '+7 points'
    });
  }

  if (!sections.hasExperience) {
    suggestions.push({
      category: 'Sections',
      priority: 'high',
      text: 'Add an Experience/Work History section',
      impact: '+7 points'
    });
  }

  if (!sections.hasEducation) {
    suggestions.push({
      category: 'Sections',
      priority: 'medium',
      text: 'Add an Education section',
      impact: '+5 points'
    });
  }

  if (!sections.hasProjects) {
    suggestions.push({
      category: 'Sections',
      priority: 'low',
      text: 'Consider adding a Projects section',
      impact: '+4 points'
    });
  }

  // Formatting suggestions
  if (formattingIssues.hasTables) {
    suggestions.push({
      category: 'Formatting',
      priority: 'high',
      text: 'Remove tables - they may not parse correctly in ATS',
      impact: '+5 points'
    });
  }

  if (formattingIssues.hasIcons) {
    suggestions.push({
      category: 'Formatting',
      priority: 'medium',
      text: 'Remove icons and special characters',
      impact: '+5 points'
    });
  }

  if (formattingIssues.hasComplexLayout) {
    suggestions.push({
      category: 'Formatting',
      priority: 'medium',
      text: 'Use a simple, single-column layout',
      impact: '+5 points'
    });
  }

  // Structure suggestions
  if (!hasBullets) {
    suggestions.push({
      category: 'Structure',
      priority: 'high',
      text: 'Use bullet points for experience and achievements',
      impact: '+4 points'
    });
  }

  if (actionVerbCount < 5) {
    suggestions.push({
      category: 'Content',
      priority: 'medium',
      text: 'Start bullet points with strong action verbs (e.g., Developed, Implemented, Managed)',
      impact: '+6 points'
    });
  }

  if (pageCount > 2) {
    suggestions.push({
      category: 'Length',
      priority: 'medium',
      text: `Reduce resume to 1-2 pages (currently ${pageCount} pages)`,
      impact: '+5 points'
    });
  } else if (pageCount < 1) {
    suggestions.push({
      category: 'Length',
      priority: 'low',
      text: 'Consider expanding your resume with more details',
      impact: '+5 points'
    });
  }

  // Sort by priority
  const priorityOrder = { high: 1, medium: 2, low: 3 };
  suggestions.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);

  return suggestions;
};

/**
 * Get explanation of how ATS score is calculated
 * @returns {Object} - Explanation object
 */
export const getATSScoreExplanation = () => {
  return {
    title: 'How This ATS Score Is Calculated',
    description: 'This tool uses rule-based logic to analyze your resume, similar to how Applicant Tracking Systems (ATS) work. No AI is used, and all analysis happens on your device.',
    breakdown: [
      {
        category: 'Keyword Matching (40 points)',
        description: 'Compares your resume against job description keywords including skills, technologies, and key phrases.',
        howItWorks: 'Higher score = more matching keywords found in your resume'
      },
      {
        category: 'Section Presence (25 points)',
        description: 'Checks for standard resume sections that ATS systems look for.',
        sections: [
          { name: 'Experience', points: 7 },
          { name: 'Skills', points: 7 },
          { name: 'Education', points: 5 },
          { name: 'Projects', points: 4 },
          { name: 'Certifications', points: 2 }
        ]
      },
      {
        category: 'Formatting & ATS Friendliness (20 points)',
        description: 'Checks for formatting issues that may cause problems with ATS parsing.',
        deductions: [
          { issue: 'Tables', points: -5 },
          { issue: 'Icons/Special Characters', points: -5 },
          { issue: 'Complex Layout', points: -5 }
        ]
      },
      {
        category: 'Structure & Quality (15 points)',
        description: 'Evaluates resume structure and content quality.',
        criteria: [
          { name: 'Optimal length (1-2 pages)', points: 5 },
          { name: 'Uses bullet points', points: 4 },
          { name: 'Strong action verbs', points: 6 }
        ]
      }
    ],
    privacy: [
      'No AI or machine learning used',
      'No data uploaded to servers',
      'All analysis happens in your browser',
      'Your resume never leaves your device'
    ]
  };
};
